// Client typeahead for the invoice editor. Replaces the legacy jQuery UI
// autocomplete; keyboard navigable and announces itself to assistive tech.

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { api } from '../lib/api';
import { useDebounced, useDismiss } from '../lib/hooks';
import { useT } from '../lib/i18n';
import type { Client } from '@shared/types';

export function ClientCombobox({ value, onSelect, error, disabled }: {
  value: Client | undefined;
  onSelect: (client: Client | undefined) => void;
  error?: string;
  disabled?: boolean;
}) {
  const t = useT();
  const [term, setTerm] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Client[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const query = useDebounced(term, 220);
  const containerRef = useDismiss(open, () => setOpen(false));

  // Reflect an externally-set client (e.g. when an existing invoice loads).
  useEffect(() => { setTerm(value?.name ?? ''); }, [value?.id, value?.name]);

  useEffect(() => {
    // The selected name sitting in the box is not a search worth re-running.
    if (!open || query.trim().length < 2 || query === value?.name) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    api.searchClients(query.trim())
      .then((results) => { if (!cancelled) { setOptions(results); setActiveIndex(0); } })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [query, open, value?.name]);

  function choose(client: Client) {
    onSelect(client);
    setTerm(client.name);
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && open && options[activeIndex]) {
      event.preventDefault();
      choose(options[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const showList = open && term.trim().length >= 2 && term !== value?.name;

  return (
    <div className="combobox" ref={containerRef}>
      <input
        ref={inputRef}
        className={`input${error ? ' input--error' : ''}`}
        role="combobox"
        aria-expanded={showList}
        aria-controls="client-options"
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        placeholder={t('invoice.field.clientPlaceholder')}
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
          // Editing the text abandons the previous selection, so a half-typed
          // name can never be saved against the client that was there before.
          if (value && e.target.value !== value.name) onSelect(undefined);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {showList && (
        <div className="combobox__list" id="client-options" role="listbox">
          {searching && options.length === 0 ? (
            <div className="combobox__empty">{t('common.loading')}</div>
          ) : options.length === 0 ? (
            <div className="combobox__empty">{t('common.noResults')}</div>
          ) : (
            options.map((client, index) => (
              <div
                key={client.id}
                role="option"
                aria-selected={index === activeIndex}
                className={`combobox__option${index === activeIndex ? ' combobox__option--active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => { e.preventDefault(); choose(client); }}
              >
                <div className="combobox__option-name">{client.name}</div>
                <div className="combobox__option-meta">
                  {[client.taxId, client.phone, client.email].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
